import Autocomplete from "@/components/Autocomplete";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { pastPresidentAssigneeName } from "../_utils/pastPresidentAssigneeName";
import { PAST_PRESIDENT_PERSON_SEARCH_QUERY, type PastPresidentPersonSearchNode } from "./queries";

const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const HITS_PER_PAGE = 10;

export type { PastPresidentPersonSearchNode };

export type PastPresidentPersonSearchProps = {
  label?: string;
  disabled?: boolean;
  onSelect: (person: PastPresidentPersonSearchNode) => void;
  onClear?: () => void;
};

export default function PastPresidentPersonSearch({
  label = "Add Past President",
  disabled = false,
  onSelect,
  onClear
}: PastPresidentPersonSearchProps) {
  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedPerson, setSelectedPerson] = useState<PastPresidentPersonSearchNode | null>(null);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setDebouncedQuery("");
      return;
    }
    const timer = setTimeout(() => setDebouncedQuery(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const skip = !canViewPeople || debouncedQuery.length < MIN_QUERY_LENGTH;
  const { data, loading, error } = useQuery(PAST_PRESIDENT_PERSON_SEARCH_QUERY, {
    variables: { search: debouncedQuery, first: HITS_PER_PAGE },
    skip,
    fetchPolicy: "no-cache"
  });

  const people = skip || error ? [] : (data?.fuzzySearchPeople?.nodes ?? []);
  const searchFailed = canViewPeople && Boolean(error) && debouncedQuery.length >= MIN_QUERY_LENGTH;
  const missingViewPeople = !canViewPeople;

  return (
    <Autocomplete<PastPresidentPersonSearchNode>
      label={label}
      placeholder="Search by name or PGA ID"
      query={query}
      data={people}
      loadingResults={loading && !skip}
      disabled={disabled || missingViewPeople}
      constrainMenuWidth
      menuPositioning="fixed"
      menuClassName="max-h-80"
      data-testid="past-presidents-person-search"
      error={searchFailed}
      errorText={searchFailed ? "Couldn't load people" : undefined}
      supportingText={missingViewPeople ? "Searching people requires the view people permission." : undefined}
      onChangeText={(value) => {
        if (selectedPerson) {
          setSelectedPerson(null);
          onClear?.();
        }
        setQuery(value);
        if (!value.trim()) {
          onClear?.();
        }
      }}
      reset={() => {
        setQuery("");
        setDebouncedQuery("");
        setSelectedPerson(null);
        onClear?.();
      }}
      onSelect={(person) => {
        const assigneeName = pastPresidentAssigneeName(person.displayName);
        setSelectedPerson(person);
        setQuery(assigneeName);
        onSelect({ ...person, displayName: assigneeName });
      }}
      renderItem={(person) => (
        <div className="flex flex-col">
          <div className="text-body-large">{person.displayName}</div>
          <div className="text-label-medium text-sys-brand-on-surface-variant">
            {[person.pgaId ? `PGA ID: ${person.pgaId}` : null, person.primarySection?.name].filter(Boolean).join(" · ")}
          </div>
        </div>
      )}
    />
  );
}
